// Phase 12 · Batch C (Amanda, Oct 23): who signs the agreement, and who is copied on emails.
//   Signer 1 is required, Signer 2 is optional, and a signer doesn't have to be one of the couple (a parent can be
//   the responsible party). Until Amanda sets signers on a couple, the two partners sign, as in Phase 11.
//   cc emails: anyone else the couple wants copied on the proposal, the agreement and the portal link.
// Shared by the dashboard, the contract template and the portal function. Pure functions only.

export const RELATIONSHIPS = ["Partner 1", "Partner 2", "Parent", "Other family", "Other"];
export const isEmail = s => /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/.test(String(s || "").trim());
const clean = (v, max = 160) => String(v == null ? "" : v).trim().slice(0, max);

/** The people who sign for the client side: 1 or 2 of them, Signer 1 first. */
export function signersOf(client) {
  const c = client || {};
  if (Array.isArray(c.signers) && c.signers.some(s => s && clean(s.name))) {
    return c.signers.filter(s => s && clean(s.name)).slice(0, 2).map(s => ({
      name: clean(s.name, 120), email: clean(s.email), phone: clean(s.phone, 40),
      relationship: RELATIONSHIPS.includes(s.relationship) ? s.relationship : "Other"
    }));
  }
  return (Array.isArray(c.partners) ? c.partners : []).slice(0, 2).map((p, i) => ({
    name: clean(p && p.name, 120), email: clean(p && p.email), phone: clean(p && p.phone, 40), relationship: i ? "Partner 2" : "Partner 1"
  })).filter(s => s.name);
}

/** True when the two partners are the ones signing (the agreement then reads exactly as Amanda wrote it). */
export function coupleIsClient(client, signers) {
  const p = ((client && client.partners) || []).map(x => clean(x && x.name).toLowerCase());
  const s = (signers || signersOf(client)).map(x => x.name.toLowerCase());
  return s.length === 2 && p.length >= 2 && s[0] === p[0] && s[1] === p[1];
}

/** "Ana Lopez (parent)" for anyone who isn't one of the couple. */
export const signerLabel = s => s.name + (/^Partner/.test(s.relationship) ? "" : " (" + s.relationship.toLowerCase() + ")");

/** Who gets the agreement, the signed copy and payment reminders: the signers. */
export const signerEmails = (client, contract) => ((contract && Array.isArray(contract.signers) && contract.signers.length ? contract.signers : signersOf(client)))
  .map(s => s.email).filter(isEmail);

/** "a@x.com, b@y.com; c@z.com" → valid, de-duplicated addresses, plus anything that isn't an address. */
export function parseCc(text) {
  const parts = String(text || "").split(/[\s,;]+/).map(x => x.trim()).filter(Boolean);
  const seen = new Set(), ok = [], bad = [];
  parts.forEach(p => { const k = p.toLowerCase(); if (seen.has(k)) return; seen.add(k); (isEmail(p) ? ok : bad).push(p); });
  return { ok: ok.slice(0, 10), bad };
}
export const ccOf = client => (Array.isArray(client && client.ccEmails) ? client.ccEmails : []).filter(isEmail).slice(0, 10);

/** Adds the cc list to a recipient list, without repeating anyone. */
export function withCc(to, client) {
  const list = Array.isArray(to) ? to : String(to || "").split(",").map(x => x.trim()).filter(Boolean);
  const seen = new Set(list.map(x => x.toLowerCase()));
  return { to: list, cc: ccOf(client).filter(x => !seen.has(x.toLowerCase())) };
}
